import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '../hooks/useDebounce';
import { 
  Filter, 
  RefreshCw, 
  Eye, 
  AlertCircle, 
  Info, 
  XCircle,
  Calendar,
  User,
  Activity,
  CreditCard,
  Users,
  Package,
  Server,
  Monitor,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/Select';
import { Label } from '../components/ui/Label';
import { apiService } from '../services/api';
import { parseDate } from '../lib/utils';
import LogDetailsModal from '../components/modals/LogDetailsModal';
import { useAuth } from '../hooks/useAuth';
import { useConfirmModal } from '../components/ui/ConfirmModal';

interface Log {
  _id: string;
  level: string;
  message: string;
  action: string;
  category: string;
  subcategory: string;
  userId: number;
  username: string;
  targetId?: number;
  targetType?: string;
  details: any;
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
}

interface LogsFilters {
  page: number;
  per: number;
  category: string;
  subcategory: string;
  level: string;
  search: string;
  /** all = ator+alvo+detalhes | actor = feitos pelo usuário | target = sobre o usuário */
  search_mode: 'all' | 'actor' | 'target';
  date_from: string;
  date_to: string;
  user_id: string;
  target_id: string;
  target_type: string;
}

function summarizeLogDetails(log: Log): string | null {
  const d = log.details || {};
  const parts: string[] = [];
  if (d.line_username) parts.push(`linha: ${d.line_username}`);
  if (d.total_cost != null) parts.push(`custo: ${d.total_cost}`);
  if (d.credits_before != null && d.credits_after != null) {
    parts.push(`créditos: ${d.credits_before} → ${d.credits_after}`);
  }
  if (d.amount != null) parts.push(`qtd: ${d.amount > 0 ? '+' : ''}${d.amount}`);
  if (d.reseller?.username) parts.push(`revendedor: ${d.reseller.username}`);
  if (d.toUser?.username) parts.push(`para: ${d.toUser.username}`);
  if (d.fromUser?.username) parts.push(`de: ${d.fromUser.username}`);
  if (d.operator?.username && d.operator.username !== log.username) {
    parts.push(`por: ${d.operator.username}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

const Logs: React.FC = () => {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { showConfirm, ConfirmModalComponent } = useConfirmModal();
  const [clearing, setClearing] = useState(false);
  const [filters, setFilters] = useState<LogsFilters>({
    page: 1,
    per: 15,
    category: '',
    subcategory: '',
    level: '',
    search: '',
    search_mode: 'actor',
    date_from: '',
    date_to: '',
    user_id: '',
    target_id: '',
    target_type: ''
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<Log | null>(null);
  const [showLogDetails, setShowLogDetails] = useState(false);

  // Debounce para search (500ms)
  const debouncedSearch = useDebounce(filters.search, 500);

  // Criar objeto de filtros com busca debounced
  const debouncedFilters = {
    ...filters,
    search: debouncedSearch
  };

  // Buscar logs
  const { data: logsData, isLoading, refetch } = useQuery({
    queryKey: ['logs', debouncedFilters],
    queryFn: () => apiService.getLogs(debouncedFilters)
  });

  // Buscar estatísticas
  const { data: statsData } = useQuery({
    queryKey: ['logs-stats'],
    queryFn: () => apiService.getLogsStats()
  });

  // Buscar categorias
  const { data: categoriesData } = useQuery({
    queryKey: ['logs-categories'],
    queryFn: () => apiService.getLogsCategories()
  });

  const logs: Log[] = (logsData as any)?.data || [];
  const pagination = (logsData as any)?.pagination || {};
  const stats = statsData || {};
  const categories = categoriesData || {};

  const handleFilterChange = (key: keyof LogsFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset para primeira página ao filtrar
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      per: 15,
      category: '',
      subcategory: '',
      level: '',
      search: '',
      search_mode: 'actor',
      date_from: '',
      date_to: '',
      user_id: '',
      target_id: '',
      target_type: ''
    });
  };

  const handleClearLogs = () => {
    if (!isAdmin()) return;
    const olderHint = filters.date_to
      ? `Serão apagados apenas logs anteriores a ${filters.date_to}`
      : 'ATENÇÃO: sem data de fim nos filtros, TODOS os logs serão apagados';
    const catHint = filters.category ? ` (categoria: ${filters.category})` : '';
    showConfirm(
      'Limpar logs do sistema',
      `${olderHint}${catHint}.\n\nNa próxima etapa digite LIMPAR para confirmar.`,
      {
        type: 'danger',
        confirmText: 'Continuar',
        cancelText: 'Cancelar',
        onConfirm: async () => {
          const typed = window.prompt('Digite LIMPAR para confirmar a exclusão dos logs:');
          if (typed !== 'LIMPAR') {
            window.alert('Confirmação cancelada. Nada foi apagado.');
            return;
          }
          setClearing(true);
          try {
            const result = await apiService.clearLogs({
              confirm: 'LIMPAR',
              older_than: filters.date_to || undefined,
              category: filters.category || undefined
            });
            window.alert(`Logs removidos: ${result?.deleted ?? 0}`);
            queryClient.invalidateQueries({ queryKey: ['logs'] });
            queryClient.invalidateQueries({ queryKey: ['logs-stats'] });
            refetch();
          } catch (err: any) {
            window.alert(err?.response?.data?.error || err?.message || 'Falha ao limpar logs');
          } finally {
            setClearing(false);
          }
        }
      }
    );
  };

  const handleViewLog = (log: Log) => {
    setSelectedLog(log);
    setShowLogDetails(true);
  };

  const handleCloseLogDetails = () => {
    setShowLogDetails(false);
    setSelectedLog(null);
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warn':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'info':
        return <Info className="w-4 h-4 text-blue-500" />;
      case 'debug':
        return <Activity className="w-4 h-4 text-gray-500" />;
      default:
        return <Info className="w-4 h-4 text-gray-500" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'auth':
        return <User className="w-4 h-4 text-green-500" />;
      case 'user':
        return <Users className="w-4 h-4 text-blue-500" />;
      case 'line':
        return <Monitor className="w-4 h-4 text-purple-500" />;
      case 'credit':
        return <CreditCard className="w-4 h-4 text-yellow-500" />;
      case 'system':
        return <Server className="w-4 h-4 text-gray-500" />;
      case 'cms':
        return <Activity className="w-4 h-4 text-indigo-500" />;
      case 'p2p':
        return <Package className="w-4 h-4 text-orange-500" />;
      case 'package':
        return <Package className="w-4 h-4 text-pink-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = parseDate(timestamp);
      if (!date) {
        return '--/--/----, --:--';
      }
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.error('Erro ao formatar timestamp:', error, timestamp);
      return '--/--/----, --:--';
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Logs do Sistema</h1>
          <p className="text-gray-600">Monitore todas as atividades do sistema</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filtros
          </Button>
          <Button
            variant="outline"
            onClick={() => refetch()}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
          {isAdmin() && (
            <Button
              variant="outline"
              onClick={handleClearLogs}
              disabled={clearing}
              className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
              {clearing ? 'Limpando...' : 'Limpar logs'}
            </Button>
          )}
        </div>
      </div>

      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg text-gray-600">Total de Logs</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg text-gray-600">Últimas 24h</p>
                <p className="text-2xl font-bold text-gray-900">{stats.recent || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg text-gray-600">Erros</p>
                <p className="text-2xl font-bold text-red-500">
                  {stats.levels?.find((l: any) => l._id === 'error')?.count || 0}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg text-gray-600">Avisos</p>
                <p className="text-2xl font-bold text-yellow-500">
                  {stats.levels?.find((l: any) => l._id === 'warn')?.count || 0}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="search">Buscar usuário / texto</Label>
              <Input
                id="search"
                placeholder="Ex: maxmau (renovações, créditos...)"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="search_mode">Escopo da busca</Label>
              <Select
                value={filters.search_mode}
                onValueChange={(value) => handleFilterChange('search_mode', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Escopo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="actor">Feitas por (ator)</SelectItem>
                  <SelectItem value="target">Sobre o usuário (alvo)</SelectItem>
                  <SelectItem value="all">Todas (ator + alvo + detalhes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="category">Categoria</Label>
              <Select value={filters.category} onValueChange={(value) => handleFilterChange('category', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas as categorias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todas as categorias</SelectItem>
                  {categories.categories?.map((cat: string) => (
                    <SelectItem key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="subcategory">Subcategoria</Label>
              <Select
                value={filters.subcategory}
                onValueChange={(value) => handleFilterChange('subcategory', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todas</SelectItem>
                  {categories.subcategories?.map((sub: string) => (
                    <SelectItem key={sub} value={sub}>
                      {sub}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="level">Nível</Label>
              <Select value={filters.level} onValueChange={(value) => handleFilterChange('level', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os níveis" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todos os níveis</SelectItem>
                  {categories.levels?.map((level: string) => (
                    <SelectItem key={level} value={level}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="per">Por página</Label>
              <Select value={filters.per.toString()} onValueChange={(value) => handleFilterChange('per', parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="date_from">Data de início</Label>
              <Input
                id="date_from"
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="date_to">Data de fim</Label>
              <Input
                id="date_to"
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={clearFilters}>
                Limpar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de logs */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xl font-medium text-gray-500 uppercase tracking-wider">
                  Nível
                </th>
                <th className="px-6 py-3 text-left text-xl font-medium text-gray-500 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xl font-medium text-gray-500 uppercase tracking-wider">
                  Mensagem
                </th>
                <th className="px-6 py-3 text-left text-xl font-medium text-gray-500 uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xl font-medium text-gray-500 uppercase tracking-wider">
                  Data/Hora
                </th>
                <th className="px-6 py-3 text-left text-xl font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Carregando logs...
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Nenhum log encontrado
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getLevelIcon(log.level)}
                        <span className="text-lg font-medium text-gray-900 capitalize">
                          {log.level}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(log.category)}
                        <span className="text-lg text-gray-900 capitalize">
                          {log.category}
                        </span>
                        {log.subcategory && (
                          <span className="text-xl text-gray-500">
                            ({log.subcategory})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-lg text-gray-900 max-w-md">
                        {log.message}
                      </div>
                      {summarizeLogDetails(log) && (
                        <div className="text-sm text-gray-600 mt-0.5 max-w-md">
                          {summarizeLogDetails(log)}
                        </div>
                      )}
                      {log.action && (
                        <div className="text-sm text-gray-400 mt-0.5">
                          Ação: {log.action}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-lg text-gray-900">
                        {log.username || 'Sistema'}
                      </div>
                      {log.userId && (
                        <div className="text-xl text-gray-500">
                          ID: {log.userId}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-500">
                      {formatTimestamp(log.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-lg font-medium">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewLog(log)}
                        className="flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        Ver
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {pagination.total_pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <Button
                variant="outline"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={!pagination.has_prev}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={!pagination.has_next}
              >
                Próximo
              </Button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-lg text-gray-700">
                  Mostrando{' '}
                  <span className="font-medium">
                    {((pagination.current_page - 1) * pagination.per_page) + 1}
                  </span>{' '}
                  até{' '}
                  <span className="font-medium">
                    {Math.min(pagination.current_page * pagination.per_page, pagination.total)}
                  </span>{' '}
                  de{' '}
                  <span className="font-medium">{pagination.total}</span>{' '}
                  resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={!pagination.has_prev}
                    className="rounded-l-md"
                  >
                    Anterior
                  </Button>
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <Button
                        key={page}
                        variant={page === pagination.current_page ? "default" : "outline"}
                        onClick={() => handlePageChange(page)}
                        className="rounded-none"
                      >
                        {page}
                      </Button>
                    );
                  })}
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={!pagination.has_next}
                    className="rounded-r-md"
                  >
                    Próximo
                  </Button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalhes do Log */}
      <LogDetailsModal
        isOpen={showLogDetails}
        onClose={handleCloseLogDetails}
        log={selectedLog}
      />
      <ConfirmModalComponent />
    </div>
  );
};

export default Logs;